import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { X, UserPlus, ChevronDown, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { AudienceMember, AudienceNode, audienceService, isPartner, nodePath } from '@/services/audienceService';
import { userService } from '@/services/userService';
import { useTranslation } from '@/hooks/useTranslation';
import Button from '@/components/ui/Button';
import DataTableWithQuery, { QueryParams } from '@/components/ui/DataTableWithQuery';
import Modal from '@/components/ui/Modal';
import CreateUserForm from '@/components/forms/CreateUserForm';
import type { CreateUserRequest, TableColumn } from '@/types';

type MemberRow = AudienceMember & Record<string, unknown>;

export default function AudienceMembersPanel({ node, nodes, onClose }: { node: AudienceNode; nodes: AudienceNode[]; onClose: () => void }) {
  const { t: { audience: c } } = useTranslation();
  const client = useQueryClient();
  const menu = useRef<HTMLDetailsElement>(null);
  const [descendants, setDescendants] = useState(false);
  const [mode, setMode] = useState<'existing' | 'new' | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialAudience = isPartner(node) ? { partnerId: node.id } : { assignments: [{ nodeId: node.id }] };
  const columns: TableColumn<MemberRow>[] = [
    { key: 'fullName', header: c.memberName, accessor: user => <Link to={`/users/${user.id}/edit`} className="cursor-pointer font-medium text-indigo-600 hover:underline">{user.fullName || user.username}</Link> },
    { key: 'username', header: c.memberUsername },
    { key: 'audience', header: c.memberGroup, accessor: user => <span className="block max-w-64 whitespace-normal break-words text-xs text-gray-500">{(user.audience?.assignments ?? []).map(assignment => nodePath(assignment.nodeId, nodes)).join(' · ') || '—'}</span> },
  ];
  function useMembers(params: QueryParams) {
    return useQuery({
      queryKey: ['audience-members', node.id, descendants, params],
      queryFn: async () => {
        const data = await audienceService.members(node.id, { descendants, search: params.search ?? '', page: params._page ?? 0, size: params._size ?? 25 });
        return { ...data, content: data.content.map(user => ({ ...user }) as MemberRow) };
      },
    });
  }
  function useCandidates(params: QueryParams) {
    return useQuery({
      queryKey: ['audience-candidates', params],
      queryFn: async () => {
        const data = await userService.getAppUsers(params);
        return { ...data, content: data.content.map(user => ({ ...user }) as MemberRow) };
      },
    });
  }
  function open(next: 'existing' | 'new') {
    if (menu.current) menu.current.open = false;
    setError(null); setSelected([]); setMode(next);
  }
  async function refresh() {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['audience-members'] }),
      client.invalidateQueries({ queryKey: ['audience-candidates'] }),
      client.invalidateQueries({ queryKey: ['users'] }),
    ]);
  }
  async function addSelected() {
    if (busy || !selected.length) return;
    setBusy(true); setError(null);
    const failed: string[] = [];
    try {
      // Keep requests bounded and retain only failed users for a safe retry.
      for (const id of selected) {
        try { await audienceService.addMember(node.id, id); }
        catch { failed.push(id); }
      }
      await refresh();
      setSelected(failed);
      if (failed.length) setError(c.membersPartialFailure);
      else { toast.success(c.memberAdded); setMode(null); }
    } catch (e) { setError(e instanceof Error ? e.message : c.loadError); }
    finally { setBusy(false); }
  }
  async function create(data: CreateUserRequest) {
    setBusy(true);
    try { await userService.createUser(data); await refresh(); toast.success(c.memberAdded); setMode(null); }
    finally { setBusy(false); }
  }
  const headerActions = <div className="flex items-center gap-2">
    <div className="flex">
      <Button size="sm" className="rounded-r-none whitespace-nowrap" onClick={() => open('existing')}><UserPlus size={16} className="mr-1" />{c.addExisting}</Button>
      <details ref={menu} className="relative" onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) event.currentTarget.open = false; }}
        onKeyDown={event => { if (event.key === 'Escape' && menu.current) menu.current.open = false; }}>
        <summary aria-label={c.add} className="flex h-full cursor-pointer list-none items-center rounded-r-md border-l border-indigo-400 bg-indigo-600 px-2 text-white hover:bg-indigo-700 [&::-webkit-details-marker]:hidden"><ChevronDown size={16} /></summary>
        <div className="absolute right-0 z-40 mt-1 min-w-48 rounded-md border border-gray-200 bg-white p-1 shadow-lg">
          <button type="button" onClick={() => open('new')} className="w-full cursor-pointer rounded px-3 py-2 text-left text-sm hover:bg-gray-100">{c.newUser}</button>
        </div>
      </details>
    </div>
    <button type="button" aria-label={c.close} className="cursor-pointer rounded p-1 text-gray-500 hover:bg-gray-100" onClick={onClose}><X size={18} /></button>
  </div>;
  return <aside className="min-w-0 self-start" aria-label={c.members}>
    <DataTableWithQuery key={String(descendants)} columns={columns} useQueryHook={useMembers} title={c.members}
      tableStyle="compact" tableId="audience-members" syncQueryString={false} searchPlaceholder={c.searchMembers} headerActions={headerActions}
      headerRenderer={({ searchQuery, onSearchChange, searchPlaceholder, headerActions: actions }) => <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3">
          <div className="relative min-w-40 flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input aria-label={searchPlaceholder} placeholder={searchPlaceholder} value={searchQuery} onChange={onSearchChange}
              className="w-full rounded-md border border-gray-300 py-1 pl-10 pr-4 text-base focus:border-indigo-500 focus:ring-indigo-500" />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600"><input className="cursor-pointer" type="checkbox" checked={descendants} onChange={e => setDescendants(e.target.checked)} />{c.includeChildren}</label>
          {actions}
      </div>} />
    <Modal isOpen={mode === 'existing'} onClose={() => { if (!busy) setMode(null); }} title={c.addUsersToGroup.replace('{name}', node.name)} size="2xl" fillBody closeOnEsc={!busy} closeOnBackdrop={!busy}
      footer={<div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" disabled={busy} onClick={() => setMode(null)}>{c.cancel}</Button>
          <Button disabled={busy || !selected.length} onClick={() => void addSelected()}>{busy ? c.loading : c.addSelectedUsers} ({selected.length})</Button>
      </div>}>
      {mode === 'existing' && <div className="flex min-h-0 flex-1 flex-col">
        {error && <p role="alert" className="rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        <div inert={busy} className="flex min-h-0 min-w-0 flex-1 flex-col">
          <DataTableWithQuery fillContainer columns={columns} useQueryHook={useCandidates} title={c.members} tableStyle="compact"
            tableId="audience-candidates" syncQueryString={false} searchPlaceholder={c.searchMembers}
            headerRenderer={({ searchQuery, onSearchChange, searchPlaceholder }) => <div className="border-b border-gray-200 px-4 py-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input aria-label={searchPlaceholder} placeholder={searchPlaceholder} value={searchQuery} onChange={onSearchChange}
                  className="w-full rounded-md border border-gray-300 py-1 pl-10 pr-4 text-base focus:border-indigo-500 focus:ring-indigo-500" />
              </div>
            </div>}
            enableRowSelection selectedRowIds={selected} onSelectionChange={setSelected} />
        </div>

      </div>}
    </Modal>
    <Modal isOpen={mode === 'new'} onClose={() => { if (!busy) setMode(null); }} title={c.newUser} size="xl" closeOnEsc={!busy} closeOnBackdrop={!busy}>
      {mode === 'new' && <CreateUserForm initialAudience={initialAudience} isSubmitting={busy} onSuccess={create} onCancel={() => { if (!busy) setMode(null); }} />}
    </Modal>
  </aside>;
}
